/**
 * GrooveEngine.js
 * Applies micro-timing adjustments (The "Pocket") based on Genre and Role.
 * Moves beyond simple global swing to instrument-specific offsets.
 */

import { getBeatStrength } from './RhythmLogic.js';
import { GROOVE_CONFIG, BASE_GROOVES } from '../data/StrategyConfig.js';

/**
 * Applies groove offsets to events.
 * @param {Array} events - Flat array of events.
 * @param {string} profileName - 'laid_back', 'dilla', etc.
 * @param {Object} context - Generation context (for barIndex/timeSig/config)
 */
export const applyGroove = (events, profileName = 'straight', context = {}) => {
    // --- REFACTORED: Procedural Config Access ---
    const config = (context.config && context.config.groove) ? context.config.groove : GROOVE_CONFIG;
    const baseTightness = config.tightnessBase || 0.5;
    const breathingAmp = config.breathingAmp || 0.005;

    // Resolve Profile (Procedural or Static)
    let profile = BASE_GROOVES[profileName] || BASE_GROOVES.straight;

    // Defect Fix: Explicit Swing Logic (Triplet Feel)
    const swingAmount = profile.swing || 0.0;

    const customOffsets = context.grooveOffsets || {};
    const timeSig = context.timeSig || [4, 4];

    return events.map(ev => {
        let offset = 0;
        const role = ev.role || '';

        // Role-based Offset Lookup (NaN Fix: Ensure defaults)
        if (role.includes('kick') || role.includes('perc_low')) offset = (customOffsets.kick !== undefined) ? customOffsets.kick : (profile.kick || 0);
        else if (role.includes('snare') || role.includes('perc_mid')) offset = (customOffsets.snare !== undefined) ? customOffsets.snare : (profile.snare || 0);
        else if (role.includes('hat') || role.includes('perc_high')) offset = (customOffsets.perc_high !== undefined) ? customOffsets.perc_high : (profile.hihat || 0);
        else if (role.includes('bass')) offset = (customOffsets.bass !== undefined) ? customOffsets.bass : (profile.bass || 0);
        else if (role.includes('melody')) offset = (customOffsets.melody !== undefined) ? customOffsets.melody : (profile.melody || 0);

        // --- ENHANCEMENT: Beat Strength Scaling ---
        // Strong beats lock tighter to grid (reduce offset).
        // Weak beats allow more "feel" (full offset).
        const beatStrength = getBeatStrength(ev.s, timeSig);

        // Scale offset based on tightness config
        // If tightnessBase is 1.0, tightness is always 1.0 (strict grid).
        // If tightnessBase is 0.5, strong beats are 0.5 tight, weak beats are 1.0 loose?
        // Actually: Tightness means REDUCING the offset.
        // If beatStrength is 1.0 (Downbeat), we want MAX tightness (Minimum offset).
        // If beatStrength is 0.0 (Offbeat), we want MIN tightness (Maximum offset).

        // Linear Interpolation:
        // effectiveOffset = offset * (1.0 - (beatStrength * baseTightness));
        // Example: Offset 20ms. Strength 1.0, BaseTight 0.5 -> Offset * 0.5 = 10ms.
        // Strength 0.0 -> Offset * 1.0 = 20ms.

        const tightnessFactor = 1.0 - (beatStrength * baseTightness);
        offset *= tightnessFactor;

        // Apply randomization if "drunk" style?
        if (profileName === 'dilla' || profileName === 'drunk') {
            offset += (Math.random() - 0.5) * (config.humanizeJitter || 0.02);
        }

        // --- ENHANCEMENT: Bar Breathing ---
        // Drift +/- over time to simulate tempo breathing
        // Use s (absolute time)
        const barPos = ev.s / 4.0;
        const breathing = Math.sin(barPos * Math.PI * 0.5) * breathingAmp;

        // Clean Grid: Store offset in playbackOffset instead of modifying s
        // Apply Swing Modifier
        const swingOffset = calculateSwing(ev.s, swingAmount);

        return {
            ...ev,
            playbackOffset: offset + breathing + swingOffset
        };
    });
};

/**
 * Explicit Swing Modifier.
 * Calculates the time offset required to swing a note at 'time'.
 * @param {number} time - Absolute time in beats.
 * @param {number} amount - Swing amount (0.0 to 1.0).
 * @returns {number} Offset in beats.
 */
export const calculateSwing = (time, amount) => {
    if (amount <= 0) return 0;

    // Position within the quarter note (0.0 - 0.99)
    const beatPos = time % 1;

    // Target the "and" of the beat (0.5).
    // Standard Swing delays the offbeat.
    // Triplet feel: 0.5 moves to 0.666 (delay of ~0.166)
    // We allow a tolerance window to catch notes slightly off-grid due to humanization/jitter
    if (Math.abs(beatPos - 0.5) < 0.15) {
        return 0.16 * amount;
    }
    return 0;
};

/**
 * Helper to apply just swing to a list of events.
 * Can be used by RhythmLogic or Quantizer.
 */
export const applySwing = (events, amount) => {
    return events.map(ev => ({
        ...ev,
        playbackOffset: (ev.playbackOffset || 0) + calculateSwing(ev.s, amount)
    }));
};
